import React, { createRef, useCallback, useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { ColumnsType } from 'antd/lib/table';
import type { CityFinanceRoundsData, ConHotFinance, HotCity } from '@/services/swagger/data-contracts';
import PintoTable from '@/components/PintoTable';
import { genEsConfig } from '@/components/charts/es-config';
// import Dot from '@/components/Dot';
import ChartHeader from '../chart-header';
import Chart from '../chart';
import TabLabel from '../TabLabel';
import CustomEmpty from '../CustomEmpty';
import type { FinancingTreeMapChartProps } from './FinancingTreeMapChart';
import styles from './index.module.scss';

export type PopularInvestorProps = {
  /** 投融资信息 */
  investorList?: ConHotFinance[];
  /** 投融资信息数据统计月份 */
  investmentDate?: string;
  /** 热门城市 */
  cityList?: HotCity[];
  /** 热门城市品牌融资分布 */
  cityFinanceRoundsList?: CityFinanceRoundsData[];
  /** 热门城市品牌融资分布统计月份 */
  cityFinanceDate?: string;
};

const FinancingTreeMapChart = dynamic<FinancingTreeMapChartProps>(
  () => import('./FinancingTreeMapChart'),
  genEsConfig({
    style: { height: 300 },
  })
);

const chartPanelPadding = 24;
const tabLabelMargin = 32;
const markLineWidth = 30;

const investorListColumns: ColumnsType<ConHotFinance> = [
  {
    title: '机构名称',
    key: 'industryTrackName',
    dataIndex: 'industryTrackName',
    render(_, record) {
      const { financeChName } = record;
      return (
        <div className={styles.investorContainer}>
          <span>{financeChName}</span>
        </div>
      );
    },
  },
  {
    title: '投资次数',
    dataIndex: 'financeNum',
    align: 'center',
    render(value) {
      return <span className="reddish-orange-text-14">{value}</span>;
    },
  },
  {
    title: '最新投资品牌',
    key: 'conBrandId',
    align: 'center',
    render(_, record) {
      const { brandChName, brandEhName } = record;
      return brandChName || brandEhName;
    },
  },
];
const PopularInvestor: React.FC<PopularInvestorProps> = ({
  investorList = [],
  investmentDate,
  cityList = [],
  cityFinanceRoundsList = [],
  cityFinanceDate,
}) => {
  console.log(cityList);
  const [selectedLabel, setSelectedLabel] = useState(cityList?.[0]);
  const [selectedLabelIndex, setSelectedLabelIndex] = useState(0);
  const [markLineStyle, setMarkeLineStyle] = useState({});
  const cityListWithRef = useMemo(
    () =>
      cityList.map(item => {
        return { ...item, ref: createRef<HTMLSpanElement>() };
      }),
    [cityList]
  );
  const calculateMarkLineStyle = useCallback(
    (index: number) => {
      const left =
        chartPanelPadding +
          cityListWithRef.slice(0, index).reduce((acc, cur) => {
            return acc + cur.ref.current.offsetWidth;
          }, 0) +
          tabLabelMargin * index +
          (cityListWithRef[index]?.ref.current.offsetWidth - markLineWidth) / 2 || 0;
      setMarkeLineStyle({ left });
    },
    [cityListWithRef]
  );
  useEffect(() => {
    calculateMarkLineStyle(selectedLabelIndex);
  }, [calculateMarkLineStyle, selectedLabelIndex]);
  return (
    <>
      <ChartHeader
        title="投融资趋势"
        titleFontSize={20}
        dateSubTitle={false}
        date={investmentDate}
        className={styles.investorChartHeader}
        subTitleClassName={styles.dateSubtitle}
      />
      <div className={styles.chartContainer}>
        <Chart header={false} panelClassName={styles.investorListChart}>
          <PintoTable
            className={styles.investorTable}
            rowKey="conFinanceId"
            dataSource={investorList}
            columns={investorListColumns}
            pagination={false}
          />
        </Chart>
        <Chart
          title="热门城市融资品牌轮次分布"
          dateSubTitle={true}
          date={cityFinanceDate}
          panelClassName={styles.popularCityChart}
        >
          {!!cityList.length ? (
            <>
              <div className={styles.cityLabelList}>
                {cityListWithRef.map((item, index) => {
                  return (
                    <TabLabel
                      ref={item.ref}
                      key={item.cityId}
                      className={styles.cityLabel}
                      name={item.cityName}
                      selected={item.cityName === selectedLabel.cityName}
                      onClick={() => {
                        setSelectedLabel(item);
                        setSelectedLabelIndex(index);
                        // const left =
                        //   24 +
                        //   cityListWithRef.slice(0, index).reduce((acc, cur) => {
                        //     return acc + cur.ref.current.offsetWidth;
                        //   }, 0) +
                        //   32 * index +
                        //   (cityListWithRef[index].ref.current.offsetWidth - 30) / 2;
                        // setMarkeLineStyle({ left });
                        calculateMarkLineStyle(index);
                      }}
                    />
                  );
                })}
              </div>
              <div className={styles.splitLine} />
              <div className={styles.markLine} style={markLineStyle} />
              {/*<div className={styles.totalBrandNumContainer}>*/}
              {/*<Dot />*/}
              {/*<span className={styles.totalBrandNumText}>品牌总数量</span>*/}
              {/*<span className="reddish-orange-text-18">{cityFinanceRoundsList[selectedLabelIndex]?.totalNum}</span>*/}
              {/*</div>*/}
              <FinancingTreeMapChart
                key={selectedLabelIndex}
                data={cityFinanceRoundsList[selectedLabelIndex]?.financeData}
              />
            </>
          ) : (
            <CustomEmpty className={styles['cityChart--empty']} />
          )}
        </Chart>
      </div>
    </>
  );
};

export default PopularInvestor;
